import type { z } from "zod/v4";
import { Flex } from "@/components/Flex/Flex";
import { Stack } from "@/components/Stack/Stack";
import { Text } from "@/components/Text";
import type { OnboardingChannel, OnboardingSchema } from "@/schemas/sections";
import { ArrowIcon } from "./icons";

export const CHANNEL_EMOJI: Record<OnboardingChannel["id"], string> = {
  "tg-channel": "📣",
  vk: "🅥",
  youtube: "▶️",
  dzen: "🟡",
  custom: "✨",
};

/** The post-connect step: a short list of Elena's owned channels, one tap each.
 *  Deliberately compact — the bot's own welcome sequence is the primary
 *  onboarding, this is the "somewhere to go next" while the user is still here.
 *  Keep it to 3-5 channels; past that the choice itself costs follows. */
export function OnboardingStep({ cfg }: { cfg: z.input<typeof OnboardingSchema> }) {
  const channels = cfg.channels ?? [];
  if (channels.length === 0) return null;
  return (
    <div data-component="OnboardingStep" className="mx-auto mt-6 max-w-xl md:mt-8">
      <div className="mb-4 text-center">
        {cfg.eyebrow && (
          <Text variant="label" as="p" className="text-muted">
            {cfg.eyebrow}
          </Text>
        )}
        <Text variant="subheading" as="h2" className="mt-1 text-fg">
          {cfg.title}
        </Text>
        {cfg.subtitle && (
          <Text variant="body-sm" as="p" className="mx-auto mt-1 max-w-lg text-muted">
            {cfg.subtitle}
          </Text>
        )}
      </div>
      <Stack gap={8}>
        {channels.map((channel) => (
          <Flex
            as="a"
            key={channel.href}
            href={channel.href}
            target="_blank"
            rel="noopener noreferrer"
            direction="row"
            align="center"
            gap={12}
            data-track={`lm_next.cta.onboarding_${channel.id}`}
            className="group rounded-2xl border border-border bg-bg p-4 text-left no-underline transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
          >
            <Flex
              as="span"
              aria-hidden="true"
              align="center"
              justify="center"
              className="h-11 w-11 shrink-0 rounded-xl bg-brand/10 text-(length:--type-heading)"
            >
              {channel.emoji ?? CHANNEL_EMOJI[channel.id]}
            </Flex>
            <div className="min-w-0 flex-1">
              <p className="font-(weight:--fw-bold) text-fg">{channel.title}</p>
              <p className="mt-0.5 text-(length:--type-body-sm) leading-(--lh-body) text-muted">
                {channel.description}
              </p>
            </div>
            <Flex
              as="span"
              align="center"
              gap={4}
              className="shrink-0 font-(weight:--fw-bold) text-brand"
            >
              <span className="hidden md:inline">{channel.cta}</span>
              <ArrowIcon />
            </Flex>
          </Flex>
        ))}
      </Stack>
      {cfg.note && (
        <Text variant="body-sm" as="p" className="mt-3 text-center text-muted">
          {cfg.note}
        </Text>
      )}
    </div>
  );
}
